// チャット履歴（セッション）の保存・復元。
//
// 【重要】ローカルLLMの会話をクラウド(Firestore/Google)へ出さない。
// Gateway が動く機体（Mac/Windows）のローカルのログファイルにだけ残す。
// ブラウザは Mac/Win のディスクへ直接書けないため、Gateway の /sessions
// エンドポイント経由で読み書きする（保存の実体は gateway/log-store.js）。
//
// - どのユーザーのログかは Gateway が「検証済みトークンの uid」で判定する。
//   このクライアントは uid を送らない（他人のログは指定すらできない）。
// - Gateway が停止中でも本体機能は止めない（load は空配列、save/remove は無視）。
// - 「最後に開いていた会話」だけは端末ローカルの関心事なので sessionStorage のまま。
// - 旧形式（sessionStorage / Firestore）の履歴は引き継がない。
import { auth } from '@/firebase/config';
import { GATEWAY_URL } from '../config';
import { asWriteSuccess } from '../tools/types';
import type { ChatMessage, ChatSession } from '../types';

const CURRENT_KEY = 'airs-ai-secretary-current';
/** 旧形式（sessionStorage 保存）のキー。見つけたら破棄する（引き継がない）。 */
const LEGACY_SESSIONS_KEY = 'airs-ai-secretary-sessions';
const LEGACY_HISTORY_KEY = 'airs-ai-secretary-history';

/** セッションごとの最大発言数。古いものから捨てる。 */
const MAX_MESSAGES = 50;

/** Firebase IDトークンを付けた認証ヘッダ。未ログイン・取得失敗なら空（Gatewayが401で弾く）。 */
async function authHeader(): Promise<Record<string, string>> {
  try {
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** 復元時の型ガード。壊れたデータや手で書き換えられたデータを弾く。 */
function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  return (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string';
}

/**
 * 復元した発言から表示に使う項目だけを組み立てる。
 * writes（結果カード）は asWriteSuccess で1件ずつ検証し、形の壊れた要素は捨てる
 * （結果カードは「実際のツール実行結果からのみ描画する」原則をログ復元時にも守る）。
 */
function toChatMessage(m: ChatMessage): ChatMessage {
  const rawWrites = (m as { writes?: unknown }).writes;
  const writes = Array.isArray(rawWrites)
    ? rawWrites.map(asWriteSuccess).filter((w): w is NonNullable<typeof w> => w !== null)
    : [];
  return {
    role: m.role,
    content: m.content,
    ...(writes.length > 0 ? { writes } : {}),
  };
}

/** Gateway の応答1件 → ChatSession。形が壊れていれば null。 */
function toSession(value: unknown): ChatSession | null {
  if (typeof value !== 'object' || value === null) return null;
  const s = value as Record<string, unknown>;
  if (typeof s.id !== 'string' || typeof s.title !== 'string') return null;
  if (typeof s.updatedAt !== 'string' || !Array.isArray(s.messages)) return null;
  return {
    id: s.id,
    title: s.title,
    updatedAt: s.updatedAt,
    messages: s.messages.filter(isChatMessage).map(toChatMessage),
  };
}

/**
 * 旧形式（sessionStorage 保存）の残骸を消す。
 * 中身の所有者を検証できないため引き継がない
 * （共用PCで前の利用者の会話を別ユーザーに紐付けてしまう恐れがある）。
 */
function dropLegacyStorage(): void {
  try {
    window.sessionStorage.removeItem(LEGACY_SESSIONS_KEY);
    window.sessionStorage.removeItem(LEGACY_HISTORY_KEY);
  } catch {
    // 消せなくても実害なし（読み込まれることはない）
  }
}

/**
 * 本人のセッションを新しい順で返す。失敗時は空配列（例外は投げない）。
 * userId は互換のため残すが、実際の本人判定は Gateway がトークンで行う（クライアントは送らない）。
 */
export async function loadSessions(_userId?: string): Promise<ChatSession[]> {
  dropLegacyStorage();
  try {
    const res = await fetch(`${GATEWAY_URL}/sessions`, { headers: await authHeader() });
    if (!res.ok) return [];
    const body = (await res.json()) as { sessions?: unknown[] };
    return (body.sessions ?? [])
      .map(toSession)
      .filter((s): s is ChatSession => s !== null);
  } catch {
    return [];
  }
}

/** 会話を1件保存する。空の会話は保存しない。失敗しても本体機能は止めない。 */
export async function saveSession(_userId: string, session: ChatSession): Promise<void> {
  if (session.messages.length === 0) return;
  try {
    await fetch(`${GATEWAY_URL}/sessions/${encodeURIComponent(session.id)}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify({
        title: session.title,
        updatedAt: session.updatedAt,
        messages: session.messages.slice(-MAX_MESSAGES),
      }),
    });
  } catch {
    // 保存失敗は致命的でないため無視（会話自体は画面上で続けられる）
  }
}

/** 会話を1件削除する。本人以外のログは Gateway 側（uid）で守られる。 */
export async function removeSession(id: string): Promise<void> {
  try {
    await fetch(`${GATEWAY_URL}/sessions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await authHeader(),
    });
  } catch {
    // 削除失敗は無視（UI 上は消えており、次回ロードで整合する）
  }
}

/** 最後に開いていたセッションのID。端末ローカルの関心事なので sessionStorage に置く。 */
export function loadCurrentId(): string | null {
  try {
    return window.sessionStorage.getItem(CURRENT_KEY);
  } catch {
    return null;
  }
}

export function saveCurrentId(id: string): void {
  try {
    window.sessionStorage.setItem(CURRENT_KEY, id);
  } catch {
    // 無視（次回は最新セッションから開くだけ）
  }
}
